import { ChartWindowState, Size } from '@ardoq/graph';
import { getBubbleChartExtent } from './chartUtils';
import { pick } from 'lodash';
import {
  assembleRenderingParameters,
  buildScalers,
  createRenderingParameters,
  resolveShowBubblesWithZeroValue,
} from './createRenderingParameters';
import {
  applyInteractiveStatesToData,
  getDecollidedDataPointLabels,
  positionAndCullDataPoints,
  sizeDataPoints,
} from './chartAreaUtils';
import {
  getQuadrantSettings,
  createQuadrantWindowStates,
  getDataPointsByQuadrant,
  resolveIncludeAllDescendants,
} from './calculations';
import {
  BubbleChartCommands,
  BubbleChartDataPointModel,
  BubbleChartEmptyStateData,
  BubbleChartRenderingData,
  BubbleChartViewModelSourceData,
  BubbleChartViewSettings,
  FieldData,
  HighlightState,
  QuadrantZoomState,
} from './types';
import {
  DEFAULT_CHART_WINDOW_STATE,
  DOMAIN_RELEVANT_VIEW_SETTINGS,
  VIEW_SETTINGS_IGNORED_IN_BUBBLE_CHART_VIEW_MODEL_SOURCE,
} from './consts';

const resetChart = (
  state: BubbleChartRenderingData
): BubbleChartRenderingData => {
  const { domain, margin, dimensions } = getBubbleChartExtent({ ...state });

  const newState = {
    ...state,
    domain,
    margin,
    dimensions,
  };

  return {
    ...newState,
    chartRenderingParameters: createRenderingParameters(newState),
  };
};

const updateInteractiveStates = (
  state: BubbleChartRenderingData,
  [hoverState, focusState]: [HighlightState, HighlightState]
): BubbleChartRenderingData => {
  if (!state.chartRenderingParameters) {
    return state;
  }
  const {
    positionedDataPoints,
    decollidedLabels,
    chartAreaProps: previousChartProps,
  } = state.chartRenderingParameters;
  const dimensions = state.dimensions;

  const { renderedDataPoints, renderedLabels } = applyInteractiveStatesToData({
    dataPoints: positionedDataPoints,
    labels: decollidedLabels,
    hoverState,
    focusState,
    dimensions,
  });

  return {
    ...state,
    chartRenderingParameters: {
      ...state.chartRenderingParameters,
      chartAreaProps: {
        ...previousChartProps,
        renderedDataPoints,
        renderedLabels,
      },
    },
    hoverState,
    focusState,
  };
};

const updateViewSettings = (
  previousState: BubbleChartRenderingData,
  viewSettings: BubbleChartViewSettings
): BubbleChartRenderingData => {
  const isChartDomainChange = DOMAIN_RELEVANT_VIEW_SETTINGS.some(
    setting => previousState.viewSettings[setting] !== viewSettings[setting]
  );

  const stateWithSettings = {
    ...previousState,
    viewSettings,
  };

  if (!isChartDomainChange) {
    return stateWithSettings;
  }

  const { background, customQuadrantSettings } = viewSettings;
  const quadrantSettings = getQuadrantSettings(
    background,
    customQuadrantSettings,
    previousState.timeLayoutLabels
  );

  const stateWithQuadrantSettings = {
    ...stateWithSettings,
    quadrantSettings,
  };

  const newChartState = resetChart(stateWithQuadrantSettings);

  return {
    ...stateWithQuadrantSettings,
    ...newChartState,
  };
};

const addQuadrantData = (
  state: BubbleChartRenderingData
): BubbleChartRenderingData => {
  const dataPointsByQuadrant = getDataPointsByQuadrant(
    state.data,
    state.domain
  );
  const quadrantWindowStates = state.chartRenderingParameters
    ? createQuadrantWindowStates({
        quadrantData: dataPointsByQuadrant,
        domains: state.domain,
        scalers: state.chartRenderingParameters.scalers,
      })
    : null;

  return {
    ...state,
    quadrantZoomState: { ...state.quadrantZoomState, quadrantWindowStates },
  };
};

const handleQuadrantSelection = (
  state: BubbleChartRenderingData,
  viewSettings: BubbleChartViewSettings
): BubbleChartRenderingData => {
  const quadrantWindowState = viewSettings.selectedQuadrant
    ? state.quadrantZoomState.quadrantWindowStates![
        viewSettings.selectedQuadrant
      ]
    : DEFAULT_CHART_WINDOW_STATE;

  if (!quadrantWindowState) {
    return state;
  }

  const dataPointsByQuadrant = getDataPointsByQuadrant(
    state.data,
    state.domain
  );

  const selectedQuadrantData = viewSettings.selectedQuadrant
    ? dataPointsByQuadrant[viewSettings.selectedQuadrant]
    : state.data;

  const quadrantZoomState = {
    ...state.quadrantZoomState,
    selectedQuadrant: viewSettings.selectedQuadrant || null,
  };

  const newState = {
    ...state,
    ...quadrantWindowState,
    selectedQuadrantData,
    quadrantZoomState,
  };

  return {
    ...newState,
    chartRenderingParameters: createRenderingParameters(newState),
  };
};

const buildRenderingData = (
  previousState: BubbleChartRenderingData,
  [clientRect, viewModelSourceData]: [Size, BubbleChartViewModelSourceData]
): BubbleChartRenderingData => {
  const {
    data,
    viewSettings: sourceDataViewSettings,
    viewInterface,
  } = viewModelSourceData;

  const viewSettingsIgnoredInSourceData = pick(
    previousState.viewSettings,
    VIEW_SETTINGS_IGNORED_IN_BUBBLE_CHART_VIEW_MODEL_SOURCE
  );

  const newViewSettings = {
    ...sourceDataViewSettings,
    ...viewSettingsIgnoredInSourceData,
  };

  const newState = {
    ...previousState,
    ...viewModelSourceData,
    viewInterface: {
      ...previousState.viewInterface,
      ...viewInterface,
    },
    clientRect,
    viewSettings: newViewSettings,
    quadrantSettings: getQuadrantSettings(
      newViewSettings.background,
      newViewSettings.customQuadrantSettings,
      viewModelSourceData.timeLayoutLabels
    ),
  };

  if (!data.length) {
    return newState;
  }

  const stateWithQuadrants = addQuadrantData(resetChart(newState));

  return handleQuadrantSelection(stateWithQuadrants, newViewSettings);
};

const withResetQuadrantSelection = (
  previousState: BubbleChartRenderingData,
  windowState: Partial<ChartWindowState>
): BubbleChartRenderingData => ({
  ...previousState,
  ...windowState,
  selectedQuadrantData: null,
  quadrantZoomState: {
    selectedQuadrant: null,
    quadrantWindowStates: previousState.quadrantZoomState.quadrantWindowStates,
  },
});

const hasRenderableChart = (state: BubbleChartRenderingData): boolean =>
  Boolean(
    state.dimensions.chart.height &&
    state.dimensions.chart.width &&
    state.data.length
  );

const handleSetScale = (
  previousState: BubbleChartRenderingData,
  chartWindowState: ChartWindowState
): BubbleChartRenderingData => {
  const updatedState = withResetQuadrantSelection(
    previousState,
    chartWindowState
  );

  if (!hasRenderableChart(updatedState)) {
    return previousState;
  }

  return {
    ...updatedState,
    chartRenderingParameters: createRenderingParameters(updatedState),
  };
};

const handleSetCenter = (
  previousState: BubbleChartRenderingData,
  windowCenter: ChartWindowState['windowCenter']
): BubbleChartRenderingData => {
  const updatedState = withResetQuadrantSelection(previousState, {
    windowCenter,
  });

  if (!hasRenderableChart(updatedState)) {
    return previousState;
  }

  const scaleContext = buildScalers(updatedState);

  // A pan keeps the zoom scale and renders the full dataset, so previously sized
  // points can be reused — unless the previous render was scoped to a quadrant,
  // in which case it only sized that subset and we must size all points again.
  const wasQuadrantScoped = Boolean(previousState.selectedQuadrantData);
  const reusableSizedDataPoints = wasQuadrantScoped
    ? undefined
    : previousState.chartRenderingParameters?.sizedDataPoints;

  const sizedDataPoints =
    reusableSizedDataPoints ??
    sizeDataPoints({
      data: updatedState.data,
      scalers: scaleContext.scalers,
      viewSettings: updatedState.viewSettings,
      isViewpointMode: !!updatedState.viewpointModeAttributes,
    });

  const positionedDataPoints = positionAndCullDataPoints({
    sizedDataPoints,
    scalers: scaleContext.scalers,
    dimensions: updatedState.dimensions,
    showBubblesWithZeroValue: resolveShowBubblesWithZeroValue(
      updatedState.viewSettings
    ),
  });

  // Decollision is skipped during the pan and runs once on pan end (panSettle).
  return {
    ...updatedState,
    chartRenderingParameters: assembleRenderingParameters(
      updatedState,
      scaleContext,
      sizedDataPoints,
      positionedDataPoints,
      []
    ),
  };
};

const panSettle = (
  state: BubbleChartRenderingData
): BubbleChartRenderingData => {
  if (!state.chartRenderingParameters || !hasRenderableChart(state)) {
    return state;
  }

  const { positionedDataPoints, chartAreaProps } =
    state.chartRenderingParameters;

  const decollidedLabels = getDecollidedDataPointLabels({
    positionedDataPoints,
    dimensions: state.dimensions,
  });

  const { renderedDataPoints, renderedLabels } = applyInteractiveStatesToData({
    dataPoints: positionedDataPoints,
    labels: decollidedLabels,
    hoverState: state.hoverState,
    focusState: state.focusState,
    dimensions: state.dimensions,
  });

  return {
    ...state,
    chartRenderingParameters: {
      ...state.chartRenderingParameters,
      decollidedLabels,
      chartAreaProps: {
        ...chartAreaProps,
        renderedDataPoints,
        renderedLabels,
      },
    },
  };
};

const updateCommands = (
  state: BubbleChartRenderingData,
  bubbleChartCommands: BubbleChartCommands
): BubbleChartRenderingData => {
  const newState = { ...state, bubbleChartCommands };

  if (!hasRenderableChart(newState)) {
    return newState;
  }

  return {
    ...newState,
    chartRenderingParameters: createRenderingParameters(newState),
  };
};

const updateGetContentContainer = (
  state: BubbleChartRenderingData,
  getBubbleChartContentContainer: () => HTMLDivElement | null
): BubbleChartRenderingData => ({
  ...state,
  getBubbleChartContentContainer,
});

const setSelectedQuadrant = (
  state: BubbleChartRenderingData,
  quadrantZoomState: Pick<QuadrantZoomState, 'selectedQuadrant'>
): BubbleChartRenderingData => {
  const newState = {
    ...state,
    selectedQuadrantData: null,
    quadrantZoomState: {
      ...state.quadrantZoomState,
      ...quadrantZoomState,
    },
  };

  const { selectedQuadrant } = quadrantZoomState;

  if (selectedQuadrant) {
    const dataPointsByQuadrant = getDataPointsByQuadrant(
      state.data,
      state.domain
    );
    const selectedQuadrantData = dataPointsByQuadrant[selectedQuadrant] || null;

    return {
      ...newState,
      selectedQuadrantData,
      chartRenderingParameters: createRenderingParameters({
        ...newState,
        selectedQuadrantData,
      }),
    };
  }

  return {
    ...newState,
    chartRenderingParameters: createRenderingParameters(newState),
  };
};

type GetEmptyStateDataArgs = {
  numericFields: FieldData[];
  allNumericFields: FieldData[];
  data: BubbleChartDataPointModel[];
  viewSettings: BubbleChartViewSettings;
  isViewLoading: boolean;
  isPresentationMode: boolean;
  isWorkspaceEmpty?: boolean;
  hasActiveFilter?: boolean;
  hasOnlyPointsWithZeroRadius?: boolean;
};

const getEmptyStateData = ({
  numericFields,
  allNumericFields,
  data,
  viewSettings,
  isViewLoading,
  isPresentationMode,
  isWorkspaceEmpty = false,
  hasActiveFilter = false,
  hasOnlyPointsWithZeroRadius = false,
}: GetEmptyStateDataArgs): BubbleChartEmptyStateData => {
  const noNumericFields = !allNumericFields.length;
  const selectedComponentHasNumericFields =
    !noNumericFields && numericFields.length > 0;
  const { includeAllDescendants } = viewSettings;
  const allDescendantsIncluded =
    resolveIncludeAllDescendants(includeAllDescendants) === 'all';
  const hasFieldsSelected = Boolean(
    viewSettings.selectedFieldNameX &&
    viewSettings.selectedFieldNameY &&
    viewSettings.selectedFieldNameRadius
  );
  const isViewEmpty = !isViewLoading && !data.length;

  return {
    isViewEmpty,
    selectedComponentHasNumericFields,
    isWorkspaceEmpty,
    hasActiveFilter,
    allDescendantsIncluded,
    noNumericFields,
    hasFieldsSelected,
    isPresentationMode,
    hasOnlyPointsWithZeroRadius,
  };
};

export const bubbleChartOperations = {
  updateInteractiveStates,
  updateViewSettings,
  updateCommands,
  buildRenderingData,
  handleSetScale,
  handleSetCenter,
  panSettle,
  resetChart,
  updateGetContentContainer,
  setSelectedQuadrant,
  getEmptyStateData,
};
